import React, { useState, useEffect, useMemo } from 'react';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  id?: string;
  name?: string;
  validateOnBlur?: boolean; // Whether to validate on blur
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void; // Callback for validation state
  title?: string; // Tooltip text
}

/**
 * Validates Israeli phone number formats:
 * - Mobile: 05XYYYYYYY (10 digits, starting with 05)
 * - Landline: 0[2,3,4,7,8,9]XXXXXXX (9 digits total)
 * - Short: *XXX..*XXXXX (3-5 digits after *)
 * - International: +9725YYYYYYYY (mobile) or +972[2,3,4,7,8,9]XXXXXXX (landline)
 *
 * NOTE:
 * - We DO NOT accept "any 10 digits" as valid.
 * - We validate exact Israeli prefixes.
 */
export const validateIsraeliPhone = (
  phone: string
): { isValid: boolean; error?: string } => {
  const trimmed = (phone || '').trim();

  if (!trimmed) {
    return { isValid: false, error: 'שדה חובה - יש למלא מספר טלפון' };
  }

  // Allow only digits, and optionally a leading + or *
  // (no spaces/dashes/letters). If you want to allow spaces/dashes, we can normalize them instead.
  const basicPattern = /^(\+|\*)?\d+$/;
  if (!basicPattern.test(trimmed)) {
    return {
      isValid: false,
      error: 'מספר טלפון לא תקין. מותר: ספרות בלבד (0-9), עם אפשרות להתחיל ב-+ או *',
    };
  }

  if (trimmed.length > 25) {
    return { isValid: false, error: 'מספר טלפון ארוך מדי. מותר: עד 25 תווים בלבד' };
  }

  // Short numbers: * followed by 3-5 digits
  if (trimmed.startsWith('*')) {
    const shortNumber = trimmed.slice(1);
    if (/^\d{3,5}$/.test(shortNumber)) return { isValid: true };
    return {
      isValid: false,
      error: 'מספר קצר חייב להיות * ואחריו 3-5 ספרות (לדוגמה: *1234)',
    };
  }

  // International format: +972...
  if (trimmed.startsWith('+')) {
    // Must be +972...
    if (!trimmed.startsWith('+972')) {
      return { isValid: false, error: 'מספר בינלאומי לא תקין. יש להתחיל ב-+972' };
    }

    const rest = trimmed.slice(4); // after +972

    // Mobile: +9725XXXXXXXX (9 digits after the 5)
    if (/^5\d{8}$/.test(rest)) return { isValid: true };

    // Landline: +972[2,3,4,7,8,9]XXXXXXX (8 digits total after +972, starting with 2/3/4/7/8/9)
    if (/^[234789]\d{7}$/.test(rest)) return { isValid: true };

    return {
      isValid: false,
      error:
        'מספר בינלאומי לא תקין. פורמט: +9725XXXXXXXX (נייד) או +9722/3/4/7/8/9XXXXXXX (קווי)',
    };
  }

  // Local formats (digits only now)
  // Mobile: 05XXXXXXXX (10 digits)
  if (/^05\d{8}$/.test(trimmed)) return { isValid: true };

  // Landline: 0[2,3,4,7,8,9]XXXXXXX (9 digits)
  if (/^0[234789]\d{7}$/.test(trimmed)) return { isValid: true };

  // Helpful length errors (based on digits only)
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly.length > 0 && digitsOnly.length < 9) {
    return {
      isValid: false,
      error: `מספר טלפון קצר מדי (כרגע: ${digitsOnly.length}). נייד חייב 10 ספרות, קווי חייב 9 ספרות`,
    };
  }
  if (digitsOnly.length > 10) {
    return {
      isValid: false,
      error: `מספר טלפון ארוך מדי (כרגע: ${digitsOnly.length}). נייד חייב 10 ספרות, קווי חייב 9 ספרות`,
    };
  }

  return {
    isValid: false,
    error: 'מספר טלפון לא תקין. נייד: 05XXXXXXXX (10 ספרות) | קווי: 0[2/3/4/7/8/9]XXXXXXX (9 ספרות)',
  };
};

/**
 * PhoneInput - A reusable phone input component for Israeli phone numbers
 * Supports: *1234, +972501234567, 0501234567, 021234567
 * Includes built-in validation for Israeli phone formats
 */
const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = 'טלפון (לדוגמה: 0501234567, 021234567, +972501234567, *1234)',
  className = '',
  required = false,
  disabled = false,
  error: externalError,
  autoFocus = false,
  onBlur,
  onFocus,
  id,
  name,
  validateOnBlur = true,
  onValidationChange,
  title,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [internalError, setInternalError] = useState<string | undefined>();

  // Sync with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Compute validation from current local value (single source of truth)
  const computedValidation = useMemo(() => {
    if (!localValue.trim()) return { isValid: false, error: undefined as string | undefined };
    return validateIsraeliPhone(localValue);
  }, [localValue]);

  // Use external error if provided, otherwise internal, otherwise computed
  const displayError = externalError || internalError || computedValidation.error;

  const isValidPhone =
    !!localValue.trim() &&
    !displayError &&
    computedValidation.isValid;

  const runValidation = (phoneValue: string) => {
    if (!phoneValue.trim()) {
      setInternalError(undefined);
      onValidationChange?.(false, undefined);
      return;
    }
    const validation = validateIsraeliPhone(phoneValue);
    setInternalError(validation.error);
    onValidationChange?.(validation.isValid, validation.error);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value || '';
    let normalized = '';

    // Keep only digits, allow + or * only as first char, enforce max 25 chars
    for (let i = 0; i < raw.length && normalized.length < 25; i += 1) {
      const ch = raw[i];

      if (i === 0 && (ch === '+' || ch === '*')) {
        normalized += ch;
        continue;
      }
      if (/\d/.test(ch)) {
        normalized += ch;
      }
    }

    setLocalValue(normalized);
    onChange(normalized);

    // Validate in real-time if validateOnBlur=false, otherwise we keep UI neutral until blur
    if (!validateOnBlur) {
      runValidation(normalized);
    } else {
      // Clear internal error while typing (so we don't show stale errors)
      setInternalError(undefined);
      onValidationChange?.(false, undefined);
    }
  };

  const handleBlur = () => {
    if (validateOnBlur) {
      runValidation(localValue);
    }
    onBlur?.();
  };

  const handleFocus = () => {
    onFocus?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys
    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ].includes(e.key)
    ) {
      return;
    }

    // Allow Ctrl/Cmd + A, C, V, X, Z
    if (e.ctrlKey || e.metaKey) {
      if (['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
        return;
      }
    }

    // Allow numbers (0-9)
    if (/\d/.test(e.key)) return;

    // Allow + or * only at the beginning
    if ((e.key === '+' || e.key === '*') && localValue.length === 0) return;

    e.preventDefault();
  };

  const baseClassName = `w-full px-3 py-2 rounded-lg border bg-white focus:outline-none focus:ring-2 transition-all ${
    displayError
      ? 'border-red-400 focus:ring-red-300 focus:border-red-500'
      : isValidPhone
      ? 'border-green-400 focus:ring-green-300 focus:border-green-500'
      : 'border-gray-300 focus:ring-blue-300 focus:border-blue-500'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`;

  return (
    <div className="w-full">
      <input
        type="tel"
        id={id}
        name={name}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={baseClassName}
        required={required}
        disabled={disabled}
        readOnly={disabled}
        autoFocus={autoFocus}
        inputMode="tel"
        maxLength={25}
        dir="rtl"
        style={{ textAlign: 'right' }}
        title={title}
      />

      {displayError && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <span>⚠️</span> {displayError}
        </p>
      )}

      {isValidPhone && !displayError && (
        <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
          <span>✓</span> מספר טלפון תקין
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
